import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';

interface InputProps {
  workflowTemplateId: string | null;
  name?: string;
  trigger?: API.AutomationTriggerType;
}

interface OutputProps {
  automations: API.AutomationFragment[];
  error?: Error;
  hasMore: boolean;
  loading: boolean;
  moreLoading: boolean;
  fetchMore: () => void;
}

export function useGetWorkflowTemplateAutomations({
  workflowTemplateId,
  name,
  trigger,
}: InputProps): OutputProps {
  const [moreLoading, setMoreLoading] = useState(false);
  const [offsetUpdated, setOffsetUpdated] = useState(false);
  const queryVariables = {
    input: {
      workflowTemplateId: workflowTemplateId ?? '',
      name,
      trigger,
    },
  };

  const {
    data,
    loading,
    error,
    fetchMore: fetchMoreAutomations,
  } = useQuery<
    API.ListWorkflowTemplateAutomationsQuery,
    API.ListWorkflowTemplateAutomationsQueryVariables
  >(Queries.automation.listWorkflowTemplateAutomations, {
    variables: queryVariables,
    skip: isNil(workflowTemplateId),
  });

  const offset = useMemo(() => {
    setOffsetUpdated(true);
    return data?.listWorkflowTemplateAutomations?.automations?.length ?? 0;
  }, [data?.listWorkflowTemplateAutomations?.automations?.length]);

  const fetchMore = useCallback(() => {
    if (
      data?.listWorkflowTemplateAutomations?.hasMore &&
      !loading &&
      !moreLoading &&
      offsetUpdated
    ) {
      setOffsetUpdated(false);
      setMoreLoading(true);
      fetchMoreAutomations({
        variables: {
          input: {
            ...queryVariables.input,
            offset,
          },
        },
      }).finally(() => {
        setMoreLoading(false);
      });
    }
  }, [
    data?.listWorkflowTemplateAutomations?.hasMore,
    fetchMoreAutomations,
    loading,
    moreLoading,
    offset,
    offsetUpdated,
    queryVariables.input,
  ]);

  return {
    automations:
      (data?.listWorkflowTemplateAutomations?.automations as API.AutomationFragment[]) ?? [],
    hasMore: data?.listWorkflowTemplateAutomations?.hasMore ?? false,
    fetchMore,
    loading,
    moreLoading,
    error,
  };
}
